import {
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "react-toastify";
import config from "../../utils/config";
import ConfirmationDialog from "../ConfirmationDialog/ConfirmationDialog";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { useDebouncedCallback } from "use-debounce";

function SharedEvents(props) {
  const [events, setEvents] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuEvent, setMenuEvent] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [confirmationOnYes, setConfirmationOnYes] = useState(null);
  const [confirmationOnClose, setConfirmationOnClose] = useState(null);

  useEffect(() => {
    props.setTitle("Shared Events");
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchEvents();
  }, [props.organization]); // eslint-disable-line

  const fetchEvents = () => {
    axios
      .get(`${config.API_ENDPOINT}/events/shared/${props.organization}/all`)
      .then((res) => setEvents(res.data))
      .catch(() => toast.error("An unexpected error has occurred"));
  };

  const exportEvent = useDebouncedCallback(
    () => {
      axios
        .post(
          `${config.API_ENDPOINT}/events/${props.organization}/${menuEvent?._id}/export`,
          {},
          {
            headers: { "x-access-token": localStorage.getItem("access-token") },
          }
        )
        .then(() => {
          setMenuAnchorEl(null);
          toast.success("You will receive an email with your file soon");
        })
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  const showDeleteDialog = (eventId) => {
    setConfirmationMessage("Are you sure you want to delete this event?");
    setConfirmationOnClose(() => () => {
      setConfirmationOpen(false);
      setMenuAnchorEl(null);
    });
    setConfirmationOnYes(() => () => {
      setConfirmationOpen(false);
      setMenuAnchorEl(null);
      deleteEvent(eventId);
    });
    setConfirmationOpen(true);
  };

  const deleteEvent = useDebouncedCallback(
    (eventId) => {
      axios
        .delete(`${config.API_ENDPOINT}/events/shared/${props.organization}/${eventId}`, {
          headers: { "x-access-token": localStorage.getItem("access-token") },
        })
        .then(() => {
          toast.success("Success!");
          fetchEvents();
        })
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            case 404:
              toast.warn("You don't have enough permissions to delete this event");
              setConfirmationOpen(false);
              setMenuAnchorEl(null);
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  if (props.user.accessLevel > 3) return null;

  return (
    <Box>
      <Box sx={{ mb: 4, pb: 3, borderBottom: "1px solid rgba(19, 33, 68, 0.1)" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Shared Events</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Manage events shared with your organization
        </Typography>
        {events.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Chip label={`${events.length} Event${events.length !== 1 ? "s" : ""}`} size="small" />
          </Box>
        )}
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Picture</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Seats Remaining</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        No shared events available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((event) => (
                    <TableRow key={event._id} hover>
                      <TableCell>
                        {event.parentEvent.imageURL ? (
                          <img
                            alt="Event"
                            height={50}
                            width={100}
                            style={{ objectFit: "contain", borderRadius: 4 }}
                            src={`${config.IMG_ENDPOINT}/uploads/${event.parentEvent.imageURL}`}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 50,
                              width: 100,
                              backgroundColor: "rgba(19, 33, 68, 0.05)",
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 12,
                              color: "text.secondary",
                            }}
                          >
                            No image
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{event.parentEvent.name}</TableCell>
                      <TableCell sx={{ fontSize: 14 }}>{event.seatsRemaining ?? "Unlimited"}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={event.parentEvent.active ? "Active" : "Inactive"}
                          size="small"
                          color={event.parentEvent.active ? "success" : "default"}
                          variant={event.parentEvent.active ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(ev) => {
                            setMenuAnchorEl(ev.currentTarget);
                            setMenuEvent(event);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuAnchorEl != null}
        keepMounted
        onClose={() => setMenuAnchorEl(null)}
      >
        <Link
          to={`/edit-shared-event/${menuEvent?._id}`}
          style={{ color: "inherit", textDecoration: "inherit" }}
        >
          <MenuItem>View/Edit</MenuItem>
        </Link>

        <Link
          to={`/event-purchases/${menuEvent?._id}`}
          style={{ color: "inherit", textDecoration: "inherit" }}
        >
          <MenuItem>View Purchases</MenuItem>
        </Link>

        {menuEvent?.parentEvent.wishlistEnabled && (
          <Link
            to={`/wishlist/${menuEvent?._id}`}
            style={{ color: "inherit", textDecoration: "inherit" }}
          >
            <MenuItem>View Wishlist</MenuItem>
          </Link>
        )}

        <MenuItem onClick={exportEvent}>Export Participants</MenuItem>

        <MenuItem onClick={() => showDeleteDialog(menuEvent?._id)}>Delete</MenuItem>
      </Menu>

      <ConfirmationDialog
        open={confirmationOpen}
        message={confirmationMessage}
        onYes={confirmationOnYes}
        onClose={confirmationOnClose}
      />
    </Box>
  );
}

export default SharedEvents;
